if (!customElements.get('product-restock')) {
  customElements.define('product-restock', class extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.copy = JSON.parse(this.querySelector('[data-restock-i18n]').textContent);
      this.form = this.querySelector('form');
      this.button = this.form.querySelector('button');
      this.message = this.querySelector('[data-restock-message]');
      this.emailFields = this.querySelector('[data-restock-email]');
      this.phoneFields = this.querySelector('[data-restock-phone]');
      this.version = 0;
      this.setStep('email');
      this.onSubmit = this.submit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.elements.phone.addEventListener('input', () => {
        this.form.elements.phone.setCustomValidity('');
        this.form.elements.sms_consent.setCustomValidity('');
      });
      this.form.elements.sms_consent.addEventListener('change', () => this.form.elements.sms_consent.setCustomValidity(''));
      // The existing sticky button has its own cart listener: intercept only restock clicks.
      this.onStickyClick = (event) => {
        if (!this.isMainProduct || !event.target.closest('#StickyAddToCartBtn') || this.hidden) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.form.hidden) this.message.focus({ preventScroll: true });
        else this.form.elements[this.step === 'phone' ? 'phone' : 'email'].focus({ preventScroll: true });
      };
      document.addEventListener('click', this.onStickyClick, true);
      this.syncSticky();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onStickyClick, true);
      this.version++;
    }

    get isMainProduct() { return !!this.closest('product-info[id^="MainProduct"]'); }

    setLoading() {
      this.version++;
      this.button.disabled = true;
      this.message.hidden = true;
      this.syncSticky(true);
    }

    restoreAfterLoadError() {
      if (this.step === 'phone' && !this.registeredEmail) this.setStep('email');
      this.button.disabled = false;
      this.button.textContent = this.submitLabel;
      this.form.removeAttribute('aria-busy');
      this.syncSticky();
    }

    get submitLabel() { return this.step === 'phone' ? this.copy.notify_sms : this.copy.notify; }

    setStep(step, focus = false) {
      this.step = step;
      this.emailFields.hidden = this.emailFields.disabled = step !== 'email';
      this.phoneFields.hidden = this.phoneFields.disabled = step !== 'phone';
      this.button.textContent = this.submitLabel;
      if (focus) this.form.elements[step === 'phone' ? 'phone' : 'email'].focus({ preventScroll: true });
    }

    updateVariant(variant, active) {
      this.version++;
      this.dataset.variantId = variant?.id || '';
      this.dataset.active = String(!!variant && active);
      this.hidden = !variant || !active;
      this.form.hidden = false;
      this.message.hidden = true;
      this.button.disabled = false;
      this.form.removeAttribute('aria-busy');
      this.registeredEmail = null;
      this.form.elements.phone.value = '';
      this.form.elements.phone.setCustomValidity('');
      this.form.elements.sms_consent.checked = false;
      this.form.elements.sms_consent.setCustomValidity('');
      this.setStep('email');
      const label = this.querySelector('[data-restock-variant]');
      label.textContent = variant?.title || '';
      label.hidden = !variant || variant.title === 'Default Title';
      this.syncSticky(false, !variant);
    }

    syncSticky(loading = false, unavailable = !this.dataset.variantId) {
      if (!this.isMainProduct) return;
      const sticky = document.getElementById('StickyAddToCartBtn');
      if (!sticky) return;
      if (!this.stickyLabel) this.stickyLabel = sticky.innerHTML;
      sticky.dataset.restock = String(!this.hidden);
      sticky.disabled = loading || unavailable;
      if (!this.hidden) sticky.textContent = this.copy.restock_title;
      else if (unavailable) sticky.textContent = this.copy.product_unavailable;
      else sticky.innerHTML = this.stickyLabel;
    }

    normalizePhone(value) {
      let phone = value.trim().replace(/[\s.()\-]/g, '');
      if (/^00/.test(phone)) phone = '+' + phone.slice(2);
      if (/^0[67]\d{8}$/.test(phone)) phone = '+33' + phone.slice(1);
      return phone;
    }

    async request(endpoint, data) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({ data }),
          signal: controller.signal,
          credentials: 'omit',
        });
        if (response.status !== 202) {
          const body = await response.json().catch(() => ({}));
          const error = new Error('Klaviyo rejected the request');
          error.status = response.status;
          error.code = body.errors?.[0]?.code;
          error.pointer = body.errors?.[0]?.source?.pointer || '';
          throw error;
        }
      } finally { clearTimeout(timeout); }
    }

    async submit(event) {
      event.preventDefault();
      if (this.hidden || this.button.disabled || !this.dataset.variantId) return;
      if (this.step === 'email') {
        await this.submitEmail();
        return;
      }
      if (!this.registeredEmail) return;
      const phone = this.normalizePhone(this.form.elements.phone.value);
      const smsConsent = this.form.elements.sms_consent;
      if (phone && !/^\+[1-9]\d{7,14}$/.test(phone)) {
        this.form.elements.phone.setCustomValidity(this.copy.invalid_phone);
      }
      if (phone && !smsConsent.checked) {
        smsConsent.setCustomValidity(this.copy.consent_required);
      }
      if (!this.form.reportValidity()) return;
      const version = ++this.version;
      const variantId = this.dataset.variantId;
      const email = this.registeredEmail;
      const attributes = {
        email,
        phone_number: phone,
        subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
        properties: {
          ['solstice_restock_sms_' + variantId]: phone,
          ['solstice_restock_sms_' + variantId + '_at']: new Date().toISOString(),
          solstice_restock_sms_disclosure: this.querySelector('[data-restock-sms-consent-label]').textContent.trim(),
        },
      };
      this.button.disabled = true;
      this.button.textContent = this.copy.saving;
      this.form.setAttribute('aria-busy', 'true');
      this.message.hidden = true;
      try {
        await this.request('subscriptions', {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes } },
            custom_source: 'Solstice product restock SMS, variant ' + variantId,
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.smsListId } } },
        });
        if (version !== this.version || !this.isConnected) return;
        // The existing EMAIL event already waits for this variant's stock.
        // Klaviyo deduplicates another BIS request; the SMS checks this exact
        // variant/phone permission plus current SMS consent before sending.
        this.confirmRegistration(true);
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        let message = this.copy.restock_sms_error;
        if (error.pointer?.includes('phone_number')) message = this.copy.invalid_phone;
        this.showMessage(message, true);
      } finally {
        if (version === this.version && this.isConnected) {
          this.button.disabled = false;
          this.button.textContent = this.submitLabel;
          this.form.removeAttribute('aria-busy');
        }
      }
    }

    async submitEmail() {
      const email = this.form.elements.email.value.trim();
      this.form.elements.email.value = email;
      if (!this.form.reportValidity()) return;
      const version = ++this.version;
      const variantId = this.dataset.variantId;
      this.button.disabled = true;
      this.message.hidden = true;
      this.form.setAttribute('aria-busy', 'true');
      // Show the phone screen immediately while the email request is saved.
      this.setStep('phone', true);
      try {
        // A fresh email-only request must not reuse an older SMS permission for
        // this same variant. Client profile updates never grant marketing consent.
        await this.request('profiles', {
          type: 'profile', attributes: { email, properties: { ['solstice_restock_sms_' + variantId]: false } },
        });
        if (version !== this.version || !this.isConnected) return;
        const restockData = {
          type: 'back-in-stock-subscription',
          attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
          relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } },
        };
        await this.request('back-in-stock-subscriptions', restockData);
        if (version !== this.version || !this.isConnected) return;
        this.registeredEmail = email;
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        this.setStep('email', true);
        let message = this.copy.restock_error;
        if (error.status === 429) message = this.copy.restock_rate;
        else if (error.pointer?.includes('email')) message = this.copy.invalid_email;
        this.showMessage(message, true);
      } finally {
        if (version === this.version && this.isConnected) {
          this.button.disabled = false;
          this.button.textContent = this.submitLabel;
          this.form.removeAttribute('aria-busy');
        }
      }
    }

    confirmRegistration(sms = false) {
      if (!this.registeredEmail) return;
      this.showMessage(sms ? this.copy.restock_sms_success : this.copy.restock_success);
      this.form.hidden = true;
      this.message.focus({ preventScroll: true });
    }

    showMessage(message, error = false) {
      this.message.textContent = message;
      this.message.dataset.error = String(error);
      this.message.hidden = false;
    }
  });
}
